// @ts-nocheck
import React, { useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { setupInterceptorsTo } from "../../../../Componentes/API/AxiosBuilder";
import { HP_medrano, URL_API_SIA } from "../../../../Contantes/Constantes";
import { useAuth } from "../../../../Auth/State/AuthContext";
import { useBody } from "../../../../Body/state/BodyContext";
import KeyCloak from "../../../../Auth/Keycloak";

export const MAIL_SOPORTE = "soporte@frba.utn.edu.ar";

const asuntoSolicitud = (area) => `Solicitud de alta de no-reply para el área ${area.name}`;

/**
 * El cuerpo incluye el identificador del área porque los nombres pueden repetirse entre
 * ramas distintas del árbol y soporte necesita saber sobre qué grupo trabajar.
 */
const cuerpoSolicitud = ({ area, direccion, motivo, uid }) => [
    `Área: ${area.path || area.name}`,
    `Identificador del área: ${area.id}`,
    `Solicitante: ${uid}`,
    `Dirección solicitada: ${direccion}`,
    "",
    "Motivo:",
    motivo,
].join("\n");

export default function SolicitarNoReply({ areas }) {
    const { user } = useAuth();
    const { setSuccess, setMessage } = useBody();
    const axiosInstance = setupInterceptorsTo(axios.create());

    const [open, setOpen] = useState(false);
    const [areaId, setAreaId] = useState(areas[0]?.id ?? "");
    const [direccion, setDireccion] = useState("");
    const [motivo, setMotivo] = useState("");
    const [loading, setLoading] = useState(false);

    const puedeCrearTicket = KeyCloak.hasRole(["ROLE_CREAR_TICKET"]);
    const area = areas.find((a) => a.id === areaId);
    const completo = Boolean(area) && direccion.trim() !== "" && motivo.trim() !== "";

    const cuerpo = area
        ? cuerpoSolicitud({ area, direccion: direccion.trim(), motivo: motivo.trim(), uid: user.uid })
        : "";

    const cerrar = () => {
        setOpen(false);
        setDireccion("");
        setMotivo("");
    };

    const enviarTicket = async () => {
        setLoading(true);
        try {
            await axiosInstance.post(URL_API_SIA + "/os/crear/ticket", {
                alert: true,
                autorespond: true,
                source: "API",
                name: user.name,
                email: user.email,
                phone: null,
                attachments: [],
                // Soporte Técnico Usuarios resuelve el alta. Su cola se elige por sede y estas
                // solicitudes se atienden en Medrano, así que el topic queda fijo.
                topicId: HP_medrano,
                subject: asuntoSolicitud(area),
                message: cuerpo.replace(/\n/g, "<br>"),
            });
            setSuccess("La solicitud fue enviada a soporte.");
            cerrar();
        } catch (error) {
            console.error("Error al solicitar la dirección no-reply:", error);
            setMessage("No se pudo enviar la solicitud. Intente nuevamente o escriba a " + MAIL_SOPORTE);
        } finally {
            setLoading(false);
        }
    };

    const enlaceMailto = area
        ? `mailto:${MAIL_SOPORTE}?subject=${encodeURIComponent(asuntoSolicitud(area))}&body=${encodeURIComponent(cuerpo)}`
        : `mailto:${MAIL_SOPORTE}`;

    return (
        <>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                    onClick={() => setOpen(true)}
                    sx={{ textTransform: "none", color: "text.secondary", borderColor: "divider" }}
                >
                    Solicitar un no-reply
                </Button>
            </Box>

            <Dialog open={open} onClose={cerrar} maxWidth="sm" fullWidth>
                <DialogTitle>Solicitar un no-reply</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 3 }}>
                        Indique el área y el motivo para solicitar el alta del no-reply. El mismo será
                        gestionado desde el área de soporte técnico.
                    </DialogContentText>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="solicitud-area">Área</InputLabel>
                        <Select
                            labelId="solicitud-area"
                            label="Área"
                            value={areaId}
                            onChange={({ target }) => setAreaId(target.value)}
                        >
                            {areas.map((a) => (
                                <MenuItem key={a.id} value={a.id}>
                                    {a.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Dirección del no-reply"
                        value={direccion}
                        onChange={({ target }) => setDireccion(target.value)}
                        helperText="Por ejemplo: no-reply-secretaria@frba.utn.edu.ar"
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        label="Motivo de la solicitud"
                        value={motivo}
                        onChange={({ target }) => setMotivo(target.value)}
                        helperText="Indique para qué difusiones se utilizará."
                        sx={{ mb: 2 }}
                    />

                    {!puedeCrearTicket && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Su usuario no tiene permiso para generar tickets. Envíe la solicitud por
                                correo a <Link href={enlaceMailto}>{MAIL_SOPORTE}</Link>.
                            </Alert>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Texto de la solicitud:
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    padding: 2,
                                    margin: 0,
                                    fontSize: 13,
                                    fontFamily: "inherit",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {cuerpo}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={cerrar} disabled={loading} sx={{ textTransform: "none" }}>
                        Cancelar
                    </Button>
                    {puedeCrearTicket ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={enviarTicket}
                            disabled={!completo || loading}
                            sx={{ textTransform: "none" }}
                        >
                            {loading ? "Enviando..." : "Enviar solicitud"}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            component="a"
                            href={enlaceMailto}
                            disabled={!completo}
                            sx={{ textTransform: "none" }}
                        >
                            Abrir en el cliente de correo
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
